import type { CloneView, RegistryView, TrustEntry } from "@kinetiq/wire";
import { num } from "../lib/format.ts";
import s from "./primitives.module.css";

/**
 * THE THREE MARKS BESIDE A TOKEN'S NAME — is it who it says it is, how many others say the same, and has
 * anyone ever managed to sell it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * THE REPORT THIS ANSWERS, AND THE THREE MEASUREMENTS BEHIND IT
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * "Where is the verified mark next to the token names? I can't tell if these tokens are legit." Measured on
 * the live stack the same day:
 *
 *   · `is_verified` was NULL on all 42,404 rows of `mint_info`. There was no mark because there was no data:
 *     the columns existed, nothing had ever written them, and no route served them.
 *   · 29,757 of 41,332 named mints — 72% — share their symbol with at least one other mint. There are FIFTEEN
 *     mints whose symbol is `NVDA`, and 303 called `ECLIPS`. On the ranked board, 14 of 40 rows were in a
 *     duplicate-identity cohort.
 *   · Of 11,554 mints with 25+ distinct buyers in 24 h, 4 had ZERO distinct sellers and 418 more sat below a
 *     0.05 seller/buyer ratio.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * THREE MARKS, NEVER ONE SCORE — and never a filter
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * A token can be a clone without being a farm, a farm without being a clone, and unverified without being
 * either — which is every legitimate token minted this week. One combined "trust score" would report all
 * three as the same suspicion, and nobody looking at it could say which input moved it.
 *
 * NOTHING HERE HIDES A ROW, ON ANY SURFACE. A user can decline a token they can see and can never discover
 * one they cannot, so a wrong hide is permanent and silent while a wrong mark is one hover from being
 * understood. Every mark states the MECHANISM in its title — "4,838 wallets bought, 819 sold" — rather than a
 * conclusion drawn on the reader's behalf with the reasoning removed.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ THERE IS NO GREEN TICK FOR "SAFE", AND THE ABSENCE IS THE POINT
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * `verified` is rendered as a tick because a REGISTRY made a claim we can point at. Nothing else here has a
 * positive form: an unmarked token is one nothing had to say about, which is not a clean bill and is not
 * drawn as one. `null` on any of the three fields means WE HAVE NOT LOOKED and renders as nothing at all —
 * never as "no", because "the registry says no" and "nobody asked" are different facts and a UI that draws
 * them the same way is asserting something it does not know.
 */
/**
 * ⚠️ EVERY FIELD IS TESTED WITH `== null`, NEVER `=== null`, AND THAT IS NOT STYLE.
 *
 * These three fields are NEW on the wire. A client is routinely newer than the server it is talking to — a
 * static bundle is replaced the instant it is built while the API runs from a pinned release, and a browser
 * holds a cached bundle across a rollback — so `row.registry` arrives as `undefined` from any server that
 * predates the field. A `=== null` guard lets `undefined` through and the very next line dereferences it,
 * which is a TypeError thrown while rendering EVERY row: the board renders nothing at all, on a defect whose
 * only trigger is a version skew that this deployment model makes routine rather than exceptional.
 *
 * The types below say `| null` because that is what the CURRENT server sends. The runtime tests accept both,
 * because what a client must survive is not what the current server sends.
 */
export interface MarksProps {
  registry: RegistryView | null;
  clones: CloneView | null;
  trust: TrustEntry | null;
  /** The symbol, so the clone mark can name what is shared. */
  symbol: string | null;
}

/**
 * BELOW THIS MANY DISTINCT BUYERS THE EXIT MARK STAYS SILENT.
 *
 * A token with three buyers and no sellers is a token nobody has traded, not a token nobody can escape, and
 * marking it would fire on every launch in its first minute — the false positive that would make this mark
 * worthless within a day. It is deliberately far BELOW the server's decision floor of fifty: between the two
 * the server publishes real counts and reaches no verdict, and this is where that evidence becomes visible on
 * a token nothing is yet willing to convict.
 */
export const EXIT_MIN_BUYERS = 25;

/** The ratio under which "almost nobody sold" is worth saying. It matches the server's own block threshold, so
 *  the mark a user sees and the rule the ranking gate applies are the same number rather than two that drift. */
export const EXIT_LOW_RATIO = 0.05;

/** How many namesakes before the count is worth showing. `1` — because "one other token shares this symbol"
 *  is exactly the case an impersonation looks like, and a threshold above it would hide the two-mint case
 *  that matters most. */
export const CLONE_MIN = 1;

export function Marks({ registry, clones, trust, symbol }: MarksProps) {
  const chips = [
    verifiedChip(registry),
    // NAMESAKES ARE NO LONGER A CHIP. The count was on almost every row — 174 for SOL, 120 for a pump token
    // — so it read as decoration rather than as a warning, and it crowded out the marks that ARE about this
    // token's conduct. The collision is still handled where it matters: the board COLLAPSES a symbol cohort
    // into one row and says so in its header ("249 of 250 rows · 1 folded into namesakes"), which is the
    // same information at the one place a user can act on it. `cloneChip` is kept below, unused, because the
    // uri/metadata chip beside it is a different and much stronger claim and the two are easy to confuse.
    uriChip(clones),
    exitChip(trust),
  ].filter((c) => c !== null);
  if (chips.length === 0) return null;
  return <span className={s.markGroup}>{chips}</span>;
}

/** A registry's claim about IDENTITY, not about conduct: a verified token can still be a terrible trade, and
 *  the title says so rather than letting a tick imply otherwise. */
function verifiedChip(registry: RegistryView | null) {
  // `null` = nobody asked. NOT rendered as "unverified": the overwhelming majority of tokens on this board
  // have never been submitted to any registry, and drawing them as rejected would be a false accusation
  // applied at scale.
  if (registry == null || !registry.verified) return null;
  const tags = registry.tags.length > 0 ? ` · tagged ${registry.tags.join(", ")}` : "";
  return (
    <span
      key="verified"
      className={[s.mark, s.markOk].join(" ")}
      data-mark="verified"
      title={`listed and verified by the Jupiter token registry${tags}. It confirms this mint is the token it claims to be — it is NOT a judgement about the trade`}
    >
      {/* THE TICK ALONE. The word added nothing a tick does not already say, and it is the widest chip on
          the row — on a board where most rows carry three or four marks, the label was costing more
          horizontal space than the signal was worth. The meaning is in the `title`, where the caveat that a
          verified token can still be a terrible trade also lives. */}
      ✓
    </span>
  );
}

/** How many other mints wear this symbol. THE COUNT IS THE WHOLE MESSAGE — no verdict, because `USDC` is one
 *  of five mints called `USDC` and `USDT` one of eight. */
function cloneChip(clones: CloneView | null, symbol: string | null) {
  const n = clones?.symbol ?? null;
  if (n === null || n < CLONE_MIN) return null;
  return (
    <span
      key="clones"
      className={[s.mark, s.markWarn].join(" ")}
      data-mark="clones"
      title={`${num(n)} other mint${n === 1 ? "" : "s"} we have seen use the symbol ${symbol ?? "this token uses"}. A symbol is not an identity and collisions are common — USDC itself has four namesakes — so check the address, and note that the highest-ranked row for a symbol is the one with the real liquidity and volume`}
    >
      {num(n)} namesake{n === 1 ? "" : "s"}
    </span>
  );
}

/** The metadata document. A SEPARATE chip from the symbol because it is much stronger evidence and merging
 *  the two into one number would average a near-certainty with a coincidence. */
function uriChip(clones: CloneView | null) {
  const n = clones?.uri ?? null;
  if (n === null || n < CLONE_MIN) return null;
  return (
    <span
      key="uri"
      className={[s.mark, s.markBad].join(" ")}
      data-mark="clone-uri"
      title={`${num(n)} other mint${n === 1 ? "" : "s"} point at this token's exact metadata document — the same name, image and description file. One document shared by hundreds of mints is one batch minted by one operator, not a coincidence`}
    >
      copied metadata
    </span>
  );
}

/**
 * CAN ANYONE ACTUALLY EXIT — distinct wallets that bought against distinct wallets that sold, from our own
 * trade log at zero vendor cost.
 *
 * IT IS NOT A CONTRACT CHECK, AND THAT IS BECAUSE THE CONTRACT CHECK DOES NOT WORK HERE. Measured over the
 * live board, 197 of the mint-authority records are revocations and every freeze-authority record is empty:
 * launchpads revoke by construction, so "authority renounced" is the DEFAULT state of every token on Solana
 * and discriminates nothing. Participation discriminates.
 */
function exitChip(trust: TrustEntry | null) {
  // THREE SILENT STATES, AND EACH IS SILENT FOR ITS OWN REASON — none of them is a finding:
  //   `null`               no producer has ever published; nothing has measured anything.
  //   `undefined`          the server predates this field. Must render identically and must not throw.
  //   counts `null`        a pass RAN and did not count this mint — it is below the aggregate's evidence
  //                        floor, which the aggregate measures as 61% of the mints that traded. `null` is NOT COUNTED and
  //                        must never be read as COUNTED ZERO: `null < 25` coerces to `0 < 25` and would
  //                        take the same branch by accident today, and would silently start marking rows the
  //                        day the comparison moved. It is tested for, explicitly.
  if (trust == null || trust.buyers === null || trust.sellers === null) return null;
  if (trust.buyers < EXIT_MIN_BUYERS) return null;
  const ratio = trust.buyers === 0 ? null : trust.sellers / trust.buyers;
  if (ratio === null || ratio > EXIT_LOW_RATIO) return null;
  const evidence = `${num(trust.buyers)} wallets bought this token and ${num(trust.sellers)} sold it, over the last 24 hours, counted per wallet rather than per trade`;
  return trust.sellers === 0 ? (
    <span
      key="exit"
      className={[s.mark, s.markBad].join(" ")}
      data-mark="no-sellers"
      title={`${evidence}. Nobody has sold. That is what a token you cannot get out of looks like from the outside — it can also mean everyone is still holding, and this cannot tell the two apart`}
    >
      no sellers
    </span>
  ) : (
    <span
      key="exit"
      className={[s.mark, s.markWarn].join(" ")}
      data-mark="few-sellers"
      title={`${evidence}. Almost nobody is taking the other side, which is what both a honeypot and a wallet farm trading with itself look like`}
    >
      {(ratio * 100).toFixed(1)}% sold
    </span>
  );
}
